import { Button, buttonVariants } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { ErrorAlert } from "../../components/ErrorAlert";
import { dashIfEmpty } from "../../lib/format";
import { useTRPC } from "../../lib/trpc";

const COLUMNS = [
  "Title",
  "Brand",
  "Style id",
  "Type",
  "Colorway",
  "Released",
  "Retail",
  "Product id",
] as const;

/** StockX catalog search: a live read through the platform fulfillment API. */
export function StockxSearchPage() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const { q, page } = useSearch({ from: "/app/fulfillment/stockx/" });
  const [query, setQuery] = useState(q ?? "");

  // Back/forward changes the URL without remounting, so the input has to follow it.
  useEffect(() => setQuery(q ?? ""), [q]);

  const search = useQuery(
    trpc.platform.fulfillment.stockx.search.queryOptions(
      { query: q ?? "", pageNumber: page ?? 1 },
      {
        enabled: !!q,
        placeholderData: (previous) => previous,
        // A repeat visit should show the last results, not spend another StockX call.
        staleTime: 5 * 60_000,
      },
    ),
  );
  const result = search.data;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">StockX catalog</h1>
          <p className="text-sm text-muted-foreground">
            Search the live StockX catalog, then open a product to read its variants and start a
            deep fetch.
          </p>
        </div>
        <Link to="/fulfillment/stockx/queue" className={buttonVariants({ variant: "outline" })}>
          Fetch queue
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Search</CardTitle>
          <CardDescription>Style id, name, or SKU.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="flex items-center gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              const trimmed = query.trim();
              if (trimmed) void navigate({ to: ".", search: { q: trimmed, page: 1 } });
            }}
          >
            <Input
              aria-label="StockX search query"
              placeholder="Style id, name, or SKU"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            <Button type="submit" disabled={search.isFetching || !query.trim()}>
              {search.isFetching ? "Searching…" : "Search"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {search.error && <ErrorAlert message={search.error.message} />}

      {search.isFetching && !result && <Skeleton className="h-64 w-full" />}

      {result && (
        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle>
              {result.count} results · page {result.pageNumber}
            </CardTitle>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={(page ?? 1) <= 1 || search.isFetching}
                onClick={() => void navigate({ to: ".", search: { q, page: (page ?? 1) - 1 } })}
              >
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!result.hasNextPage || search.isFetching}
                onClick={() => void navigate({ to: ".", search: { q, page: (page ?? 1) + 1 } })}
              >
                Next
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {result.products.length === 0 ? (
              <p className="text-sm text-muted-foreground">No products matched this query.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {COLUMNS.map((column) => (
                        <TableHead key={column}>{column}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.products.map((product) => (
                      <TableRow key={product.productId}>
                        <TableCell>
                          <Link
                            className="underline underline-offset-4"
                            to="/fulfillment/stockx/$productId"
                            params={{ productId: product.productId }}
                          >
                            {dashIfEmpty(product.title)}
                          </Link>
                        </TableCell>
                        <TableCell>{dashIfEmpty(product.brand)}</TableCell>
                        <TableCell>{dashIfEmpty(product.styleId)}</TableCell>
                        <TableCell>{dashIfEmpty(product.productType)}</TableCell>
                        <TableCell>{dashIfEmpty(product.colorway)}</TableCell>
                        <TableCell>{dashIfEmpty(product.releaseDate)}</TableCell>
                        <TableCell>{dashIfEmpty(product.retailPrice)}</TableCell>
                        <TableCell className="font-mono text-xs">{product.productId}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
